import { twx } from '@/utils/tailwind';

export interface MapPlaceholderProps {
  className?: string;
}

/**
 * Decorative low-contrast SVG that mimics the abstract shapes of a city map
 * — streets, footpaths, parks, trees, buildings — to fill the map pane in
 * Explore mode before the user has searched.
 *
 * Layer order is back-to-front:
 *   1. Roads          — drawn first; water + parks above them hide road
 *                       segments that would otherwise cross.
 *   2. Water          — meandering river + corner lake.
 *   3. Parks          — irregular greenspaces.
 *   4. Buildings      — packed into the rectangles defined by the road
 *                       grid, so they never sit on top of a road.
 *   5. Trees / bushes — inside parks and along major street edges.
 *   6. Footpaths      — dashed trails through parks.
 *   7. Plazas         — small roundabouts at major intersections.
 *
 * Each layer leans into the kawaii pink/purple theme: roads and buildings
 * sit in pale lavender / dusty-pink territory so they read as part of the
 * background; greenspaces, water, and trees keep their natural colors but
 * stay muted enough to pop subtly rather than dominate.
 */

// Single source of truth for the pastel palette. Kept inline so the SVG
// remains self-contained (no need for CSS custom properties at the wrapper).
const COLORS = {
  // Roads — dusty lavender, blends with the pink-purple background
  roadDark: 'rgba(180, 162, 200, 0.5)',
  roadLight: 'rgba(245, 235, 250, 0.65)',
  sideStreet: 'rgba(185, 168, 205, 0.4)',
  // Water — soft blue with a hint of purple. Higher opacity so road
  // segments underneath don't bleed through where the river or lake
  // crosses streets.
  waterDark: 'rgba(160, 195, 220, 0.75)',
  waterLight: 'rgba(210, 228, 240, 0.85)',
  // Parks — soft sage green, kept low-saturation so it doesn't overpower
  parkOuter: 'rgba(170, 200, 175, 0.42)',
  parkInner: 'rgba(190, 215, 190, 0.32)',
  // Buildings — pale pink / lavender so they fit the kawaii palette
  building: 'rgba(225, 195, 220, 0.5)',
  buildingDark: 'rgba(205, 175, 200, 0.55)',
  buildingLight: 'rgba(240, 215, 230, 0.45)',
  // Trees and bushes — keep deeper green for accent
  tree: 'rgba(110, 155, 120, 0.7)',
  bush: 'rgba(140, 180, 140, 0.6)',
  // Plazas and footpaths — lavender tones
  plaza: 'rgba(200, 180, 220, 0.5)',
  plazaCenter: 'rgba(175, 160, 205, 0.55)',
  footpath: 'rgba(195, 170, 210, 0.55)',
};

export const MapPlaceholder = ({ className }: MapPlaceholderProps) => {
  return (
    <div
      className={twx(
        'w-full h-full relative overflow-hidden bg-gradient-to-br from-primary-subtle/55 via-secondary-subtle/35 to-content-background',
        className,
      )}
      aria-hidden
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1600 1200"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
      >
        <title>Map placeholder</title>

        {/* 1. ROADS — paired stroke (dark base + lighter inner stripe).
            Major arterials are straight so they stay within their stroke
            band and don't intrude into the building blocks. Visual
            variety comes from the curving side streets below. */}
        <g fill="none" strokeLinecap="round">
          {/* Major horizontals — straight at y = 178 / 540 / 850. The top
              arterial sits a bit tighter to Row 0 so it doesn't intrude into
              Row 1's building band. */}
          <line x1="-40" y1="178" x2="1640" y2="178" stroke={COLORS.roadDark} strokeWidth="14" />
          <line x1="-40" y1="178" x2="1640" y2="178" stroke={COLORS.roadLight} strokeWidth="9" />
          <line x1="-40" y1="540" x2="1640" y2="540" stroke={COLORS.roadDark} strokeWidth="12" />
          <line x1="-40" y1="540" x2="1640" y2="540" stroke={COLORS.roadLight} strokeWidth="7" />
          <line x1="-40" y1="850" x2="1640" y2="850" stroke={COLORS.roadDark} strokeWidth="14" />
          <line x1="-40" y1="850" x2="1640" y2="850" stroke={COLORS.roadLight} strokeWidth="9" />
          {/* Major verticals — straight at x = 358 / 750 / 1155. The outer
              two are nudged so they sit cleanly between the building columns
              instead of skimming the rightmost rects in Col 0 / Col 2. */}
          <line x1="358" y1="-40" x2="358" y2="1240" stroke={COLORS.roadDark} strokeWidth="13" />
          <line x1="358" y1="-40" x2="358" y2="1240" stroke={COLORS.roadLight} strokeWidth="8" />
          <line x1="750" y1="-40" x2="750" y2="1240" stroke={COLORS.roadDark} strokeWidth="14" />
          <line x1="750" y1="-40" x2="750" y2="1240" stroke={COLORS.roadLight} strokeWidth="9" />
          <line x1="1155" y1="-40" x2="1155" y2="1240" stroke={COLORS.roadDark} strokeWidth="12" />
          <line x1="1155" y1="-40" x2="1155" y2="1240" stroke={COLORS.roadLight} strokeWidth="7" />
          {/* Side streets — horizontals only, routed through the gaps
              between sub-rows of buildings so they never cut through a
              rect. Each street is broken into segments that stop at park
              boundaries so they don't slice through greenspaces either.
              Verticals were dropped because the columns don't share a
              clean unbroken gap. */}
          <g stroke={COLORS.sideStreet} strokeWidth="4" fill="none">
            {/* Row 0 inner (gap y≈85–95) — stops at the wedge park (x≈1460) */}
            <line x1="-40" y1="90" x2="1450" y2="90" />
            {/* Row 1 inner (gap y≈265–335) — three segments around the
                central park (x≈360–725) and forest patch (x≈1180–1495) */}
            <line x1="-40" y1="320" x2="355" y2="320" />
            <line x1="725" y1="320" x2="1175" y2="320" />
            <line x1="1495" y1="320" x2="1640" y2="320" />
            {/* Row 2 inner (gap y≈725–750) — picks up after the small left
                park (x≈270) and continues to the right edge */}
            <line x1="270" y1="735" x2="1640" y2="735" />
            {/* Row 3 inner (gap y≈930–980) — no parks at this y */}
            <line x1="-40" y1="970" x2="1640" y2="970" />
          </g>
        </g>

        {/* 2. WATER — a lake in the bottom-right corner. (The river that
            used to run down the left edge was removed because it kept
            tangling with the small left park and crossing every major
            horizontal arterial.) */}
        <g>
          <path
            d="M 1280 1050 Q 1240 1010 1300 990 Q 1400 975 1500 1010 Q 1580 1050 1580 1110 Q 1560 1180 1480 1200 Q 1370 1210 1290 1170 Q 1240 1130 1280 1050 Z"
            fill={COLORS.waterDark}
          />
          <path
            d="M 1300 1055 Q 1265 1020 1310 1005 Q 1395 990 1490 1020 Q 1565 1060 1560 1110 Q 1545 1165 1480 1180 Q 1380 1190 1305 1155 Q 1265 1120 1300 1055 Z"
            fill={COLORS.waterLight}
          />
        </g>

        {/* 3. PARKS — irregular organic shapes. Drawn after roads so road
            segments running through parks disappear into the greenery. */}
        <g>
          {/* Large central park (block: 360–745 × 195–540) */}
          <path
            d="M 380 220 Q 420 200 480 210 Q 580 200 640 230 Q 700 270 720 340 Q 720 420 670 470 Q 600 510 510 510 Q 430 510 400 460 Q 360 390 380 220 Z"
            fill={COLORS.parkOuter}
          />
          <path
            d="M 460 290 Q 510 270 580 285 Q 640 310 650 360 Q 645 410 600 430 Q 530 445 490 420 Q 450 380 460 290 Z"
            fill={COLORS.parkInner}
          />
          {/* Small park (block: 5–345 × 555–840) */}
          <path
            d="M 30 680 Q 60 650 110 660 Q 180 670 230 700 Q 280 740 260 790 Q 220 830 150 830 Q 80 815 30 770 Q 10 720 30 680 Z"
            fill={COLORS.parkOuter}
          />
          {/* Forest patch (right edge, 1180–1480 × 220–440) */}
          <path
            d="M 1200 260 Q 1220 220 1280 220 Q 1370 215 1430 250 Q 1490 290 1490 340 Q 1470 410 1390 430 Q 1280 440 1220 400 Q 1180 350 1200 260 Z"
            fill={COLORS.parkOuter}
          />
          {/* Pocket park (block: 360–745 × 870–955) */}
          <path
            d="M 580 880 Q 600 870 640 875 Q 680 885 690 910 Q 685 940 660 950 Q 615 955 590 940 Q 565 920 580 880 Z"
            fill={COLORS.parkOuter}
          />
          {/* Wedge park (top-right, 1460–1605 × 5–145) */}
          <path
            d="M 1470 20 Q 1500 5 1560 10 Q 1605 30 1605 80 Q 1590 130 1540 140 Q 1490 130 1465 90 Q 1450 50 1470 20 Z"
            fill={COLORS.parkOuter}
          />
        </g>

        {/* 4. BUILDINGS — packed inside each block defined by the road grid.
            Each block has its own cluster pattern, sized and positioned
            independently, so the city doesn't read as a single uniform
            grid. None of these rectangles cross a road centerline. */}
        <g>
          {/* ===== Row 0 (y: 5–175) ===== */}
          {/* Col 0 (x: 5–345) */}
          <rect x="20" y="20" width="55" height="42" rx="2" fill={COLORS.building} />
          <rect x="90" y="30" width="42" height="50" rx="2" fill={COLORS.buildingDark} />
          <rect x="160" y="22" width="50" height="38" rx="2" fill={COLORS.buildingLight} />
          <rect x="225" y="30" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="290" y="20" width="45" height="42" rx="2" fill={COLORS.buildingLight} />
          <rect x="30" y="95" width="60" height="58" rx="2" fill={COLORS.buildingDark} />
          <rect x="105" y="105" width="55" height="50" rx="2" fill={COLORS.building} />
          <rect x="180" y="100" width="65" height="62" rx="2" fill={COLORS.buildingLight} />
          <rect x="265" y="110" width="65" height="50" rx="2" fill={COLORS.building} />
          {/* Col 1 (x: 360–745) */}
          <rect x="370" y="20" width="48" height="45" rx="2" fill={COLORS.building} />
          <rect x="435" y="25" width="55" height="40" rx="2" fill={COLORS.buildingLight} />
          <rect x="510" y="20" width="42" height="50" rx="2" fill={COLORS.buildingDark} />
          <rect x="565" y="30" width="58" height="42" rx="2" fill={COLORS.building} />
          <rect x="640" y="22" width="50" height="48" rx="2" fill={COLORS.buildingLight} />
          <rect x="700" y="30" width="40" height="42" rx="2" fill={COLORS.building} />
          <rect x="380" y="95" width="68" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="465" y="100" width="55" height="55" rx="2" fill={COLORS.building} />
          <rect x="540" y="95" width="60" height="62" rx="2" fill={COLORS.buildingLight} />
          <rect x="615" y="105" width="50" height="50" rx="2" fill={COLORS.building} />
          <rect x="685" y="95" width="55" height="58" rx="2" fill={COLORS.buildingDark} />
          {/* Col 2 (x: 760–1145) */}
          <rect x="770" y="22" width="55" height="48" rx="2" fill={COLORS.building} />
          <rect x="840" y="30" width="42" height="40" rx="2" fill={COLORS.buildingLight} />
          <rect x="895" y="20" width="60" height="50" rx="2" fill={COLORS.buildingDark} />
          <rect x="970" y="28" width="50" height="42" rx="2" fill={COLORS.building} />
          <rect x="1035" y="20" width="55" height="48" rx="2" fill={COLORS.buildingLight} />
          <rect x="1100" y="30" width="40" height="40" rx="2" fill={COLORS.building} />
          <rect x="775" y="95" width="62" height="58" rx="2" fill={COLORS.buildingLight} />
          <rect x="855" y="100" width="58" height="50" rx="2" fill={COLORS.building} />
          <rect x="930" y="95" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="1000" y="100" width="60" height="55" rx="2" fill={COLORS.building} />
          <rect x="1075" y="95" width="65" height="60" rx="2" fill={COLORS.buildingLight} />
          {/* Col 3 (x: 1160–1595) — wedge park sits in the top-right corner */}
          <rect x="1170" y="20" width="55" height="48" rx="2" fill={COLORS.building} />
          <rect x="1240" y="30" width="42" height="42" rx="2" fill={COLORS.buildingLight} />
          <rect x="1295" y="22" width="58" height="48" rx="2" fill={COLORS.buildingDark} />
          <rect x="1365" y="30" width="50" height="40" rx="2" fill={COLORS.building} />
          <rect x="1175" y="95" width="65" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="1255" y="100" width="60" height="55" rx="2" fill={COLORS.building} />
          <rect x="1330" y="95" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1400" y="100" width="42" height="50" rx="2" fill={COLORS.buildingDark} />

          {/* ===== Row 1 (y: 195–540) ===== */}
          {/* Col 0 (x: 5–345) */}
          <rect x="20" y="195" width="48" height="62" rx="2" fill={COLORS.building} />
          <rect x="80" y="200" width="55" height="58" rx="2" fill={COLORS.buildingLight} />
          <rect x="170" y="200" width="45" height="65" rx="2" fill={COLORS.buildingDark} />
          <rect x="230" y="195" width="58" height="55" rx="2" fill={COLORS.building} />
          <rect x="300" y="200" width="40" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="20" y="335" width="60" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="95" y="340" width="45" height="50" rx="2" fill={COLORS.building} />
          <rect x="180" y="340" width="50" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="260" y="335" width="55" height="55" rx="2" fill={COLORS.building} />
          <rect x="20" y="450" width="50" height="60" rx="2" fill={COLORS.building} />
          <rect x="100" y="455" width="55" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="200" y="455" width="48" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="280" y="450" width="55" height="60" rx="2" fill={COLORS.building} />
          {/* Col 1 = central park, no buildings */}
          {/* Col 2 (x: 760–1145) — river dips through here; keep sparse */}
          <rect x="775" y="200" width="50" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="840" y="205" width="42" height="55" rx="2" fill={COLORS.building} />
          <rect x="895" y="195" width="55" height="62" rx="2" fill={COLORS.buildingDark} />
          <rect x="775" y="450" width="55" height="60" rx="2" fill={COLORS.building} />
          <rect x="840" y="455" width="48" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="895" y="450" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="960" y="455" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="1020" y="450" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1085" y="455" width="55" height="55" rx="2" fill={COLORS.building} />
          {/* Col 3 (x: 1160–1595) — forest patch sits roughly 1200–1480 × 220–430 */}
          <rect x="1170" y="195" width="50" height="58" rx="2" fill={COLORS.building} />
          <rect x="1505" y="195" width="50" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1560" y="200" width="38" height="55" rx="2" fill={COLORS.building} />
          <rect x="1505" y="335" width="50" height="55" rx="2" fill={COLORS.building} />
          <rect x="1560" y="340" width="40" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1170" y="455" width="55" height="60" rx="2" fill={COLORS.building} />
          <rect x="1235" y="460" width="42" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="1290" y="455" width="55" height="58" rx="2" fill={COLORS.buildingDark} />
          <rect x="1355" y="460" width="50" height="55" rx="2" fill={COLORS.building} />
          <rect x="1420" y="450" width="55" height="65" rx="2" fill={COLORS.buildingLight} />
          <rect x="1485" y="455" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="1545" y="455" width="50" height="60" rx="2" fill={COLORS.buildingDark} />

          {/* ===== Row 2 (y: 565–840) ===== */}
          {/* Col 0 — upper portion only (small park occupies lower half) */}
          <rect x="20" y="565" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="85" y="570" width="45" height="50" rx="2" fill={COLORS.building} />
          <rect x="170" y="565" width="50" height="58" rx="2" fill={COLORS.buildingDark} />
          <rect x="240" y="575" width="55" height="50" rx="2" fill={COLORS.building} />
          <rect x="305" y="570" width="40" height="55" rx="2" fill={COLORS.buildingLight} />
          {/* Col 1 (dense downtown) */}
          <rect x="370" y="570" width="48" height="48" rx="2" fill={COLORS.buildingDark} />
          <rect x="430" y="575" width="42" height="55" rx="2" fill={COLORS.building} />
          <rect x="485" y="565" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="555" y="570" width="40" height="50" rx="2" fill={COLORS.building} />
          <rect x="610" y="565" width="52" height="58" rx="2" fill={COLORS.buildingDark} />
          <rect x="675" y="575" width="60" height="50" rx="2" fill={COLORS.buildingLight} />
          <rect x="375" y="660" width="55" height="65" rx="2" fill={COLORS.building} />
          <rect x="445" y="670" width="48" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="510" y="660" width="60" height="58" rx="2" fill={COLORS.buildingLight} />
          <rect x="585" y="670" width="50" height="50" rx="2" fill={COLORS.building} />
          <rect x="650" y="660" width="58" height="62" rx="2" fill={COLORS.buildingDark} />
          <rect x="370" y="750" width="55" height="80" rx="2" fill={COLORS.buildingLight} />
          <rect x="440" y="755" width="48" height="70" rx="2" fill={COLORS.building} />
          <rect x="505" y="750" width="42" height="75" rx="2" fill={COLORS.buildingDark} />
          <rect x="560" y="760" width="55" height="65" rx="2" fill={COLORS.building} />
          <rect x="630" y="750" width="58" height="80" rx="2" fill={COLORS.buildingLight} />
          <rect x="700" y="755" width="40" height="70" rx="2" fill={COLORS.buildingDark} />
          {/* Col 2 (dense) */}
          <rect x="770" y="565" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="830" y="570" width="55" height="50" rx="2" fill={COLORS.buildingLight} />
          <rect x="895" y="565" width="42" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="950" y="575" width="58" height="50" rx="2" fill={COLORS.building} />
          <rect x="1020" y="565" width="50" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1085" y="570" width="55" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="775" y="660" width="55" height="65" rx="2" fill={COLORS.buildingDark} />
          <rect x="845" y="670" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="905" y="660" width="55" height="65" rx="2" fill={COLORS.buildingLight} />
          <rect x="975" y="670" width="50" height="55" rx="2" fill={COLORS.building} />
          <rect x="1040" y="660" width="55" height="65" rx="2" fill={COLORS.buildingDark} />
          <rect x="1105" y="665" width="40" height="55" rx="2" fill={COLORS.building} />
          <rect x="770" y="750" width="60" height="75" rx="2" fill={COLORS.building} />
          <rect x="845" y="755" width="55" height="70" rx="2" fill={COLORS.buildingLight} />
          <rect x="915" y="750" width="42" height="80" rx="2" fill={COLORS.buildingDark} />
          <rect x="970" y="760" width="55" height="70" rx="2" fill={COLORS.building} />
          <rect x="1040" y="750" width="60" height="75" rx="2" fill={COLORS.buildingLight} />
          <rect x="1115" y="755" width="30" height="70" rx="2" fill={COLORS.buildingDark} />
          {/* Col 3 */}
          <rect x="1170" y="565" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1240" y="570" width="42" height="55" rx="2" fill={COLORS.building} />
          <rect x="1295" y="565" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="1365" y="575" width="50" height="50" rx="2" fill={COLORS.building} />
          <rect x="1430" y="565" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1500" y="570" width="42" height="55" rx="2" fill={COLORS.building} />
          <rect x="1555" y="565" width="42" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="1170" y="660" width="55" height="65" rx="2" fill={COLORS.buildingDark} />
          <rect x="1240" y="670" width="45" height="55" rx="2" fill={COLORS.building} />
          <rect x="1305" y="660" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1375" y="670" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="1435" y="660" width="55" height="65" rx="2" fill={COLORS.buildingDark} />
          <rect x="1505" y="670" width="42" height="55" rx="2" fill={COLORS.building} />
          <rect x="1555" y="660" width="42" height="65" rx="2" fill={COLORS.buildingLight} />
          <rect x="1170" y="750" width="55" height="75" rx="2" fill={COLORS.building} />
          <rect x="1240" y="755" width="48" height="65" rx="2" fill={COLORS.buildingLight} />
          <rect x="1305" y="750" width="55" height="75" rx="2" fill={COLORS.buildingDark} />
          <rect x="1375" y="760" width="50" height="65" rx="2" fill={COLORS.building} />
          <rect x="1440" y="750" width="55" height="75" rx="2" fill={COLORS.buildingLight} />
          <rect x="1510" y="755" width="42" height="70" rx="2" fill={COLORS.building} />
          <rect x="1560" y="755" width="35" height="70" rx="2" fill={COLORS.buildingDark} />

          {/* ===== Row 3 (y: 865–1195) ===== */}
          {/* Col 0 */}
          <rect x="20" y="870" width="55" height="60" rx="2" fill={COLORS.building} />
          <rect x="85" y="875" width="48" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="145" y="870" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="215" y="880" width="50" height="50" rx="2" fill={COLORS.building} />
          <rect x="280" y="870" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="20" y="980" width="48" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="80" y="985" width="55" height="50" rx="2" fill={COLORS.building} />
          <rect x="170" y="980" width="50" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="245" y="985" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="305" y="980" width="40" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="20" y="1085" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="85" y="1090" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="155" y="1085" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="225" y="1095" width="50" height="50" rx="2" fill={COLORS.building} />
          <rect x="290" y="1085" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          {/* Col 1 — pocket park around (580–690 × 875–955) */}
          <rect x="370" y="870" width="45" height="55" rx="2" fill={COLORS.building} />
          <rect x="425" y="880" width="42" height="50" rx="2" fill={COLORS.buildingLight} />
          <rect x="480" y="870" width="48" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="540" y="880" width="35" height="50" rx="2" fill={COLORS.building} />
          <rect x="700" y="870" width="40" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="370" y="980" width="55" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="440" y="985" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="500" y="980" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="565" y="985" width="48" height="55" rx="2" fill={COLORS.building} />
          <rect x="625" y="980" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="695" y="985" width="45" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="370" y="1085" width="55" height="55" rx="2" fill={COLORS.building} />
          <rect x="440" y="1090" width="48" height="50" rx="2" fill={COLORS.buildingLight} />
          <rect x="500" y="1085" width="55" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="570" y="1095" width="48" height="45" rx="2" fill={COLORS.building} />
          <rect x="630" y="1085" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="700" y="1090" width="42" height="55" rx="2" fill={COLORS.building} />
          {/* Col 2 */}
          <rect x="770" y="870" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="840" y="880" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="900" y="870" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="970" y="880" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="1030" y="870" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1100" y="880" width="40" height="55" rx="2" fill={COLORS.building} />
          <rect x="770" y="980" width="55" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="840" y="985" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="900" y="980" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="970" y="985" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="1030" y="980" width="55" height="60" rx="2" fill={COLORS.buildingDark} />
          <rect x="1100" y="985" width="40" height="55" rx="2" fill={COLORS.building} />
          <rect x="770" y="1085" width="55" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="840" y="1090" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="900" y="1085" width="55" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="970" y="1090" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="1030" y="1085" width="55" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="1100" y="1090" width="40" height="50" rx="2" fill={COLORS.building} />
          {/* Col 3 — only upper sliver (lake takes the rest) */}
          <rect x="1170" y="870" width="55" height="60" rx="2" fill={COLORS.buildingLight} />
          <rect x="1240" y="880" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="1300" y="870" width="55" height="55" rx="2" fill={COLORS.buildingDark} />
          <rect x="1370" y="880" width="50" height="50" rx="2" fill={COLORS.building} />
          <rect x="1430" y="870" width="55" height="55" rx="2" fill={COLORS.buildingLight} />
          <rect x="1500" y="880" width="48" height="50" rx="2" fill={COLORS.building} />
          <rect x="1560" y="870" width="35" height="55" rx="2" fill={COLORS.buildingDark} />
        </g>

        {/* 5. TREES + BUSHES — trees are darker, bushes are smaller and
            lighter. Scattered inside parks and along major street edges. */}
        <g>
          {/* Central park */}
          <circle cx="450" cy="240" r="5" fill={COLORS.tree} />
          <circle cx="480" cy="250" r="6" fill={COLORS.tree} />
          <circle cx="525" cy="245" r="5" fill={COLORS.tree} />
          <circle cx="570" cy="240" r="6" fill={COLORS.tree} />
          <circle cx="615" cy="260" r="5" fill={COLORS.tree} />
          <circle cx="660" cy="280" r="6" fill={COLORS.tree} />
          <circle cx="700" cy="320" r="5" fill={COLORS.tree} />
          <circle cx="400" cy="350" r="5" fill={COLORS.tree} />
          <circle cx="420" cy="430" r="6" fill={COLORS.tree} />
          <circle cx="460" cy="480" r="5" fill={COLORS.tree} />
          <circle cx="555" cy="495" r="6" fill={COLORS.tree} />
          <circle cx="640" cy="490" r="5" fill={COLORS.tree} />
          <circle cx="690" cy="450" r="6" fill={COLORS.tree} />
          <circle cx="500" cy="320" r="3" fill={COLORS.bush} />
          <circle cx="540" cy="335" r="3.5" fill={COLORS.bush} />
          <circle cx="595" cy="335" r="3" fill={COLORS.bush} />
          <circle cx="610" cy="385" r="3.5" fill={COLORS.bush} />
          <circle cx="525" cy="395" r="3" fill={COLORS.bush} />
          <circle cx="565" cy="400" r="3.5" fill={COLORS.bush} />
          {/* Small left park */}
          <circle cx="80" cy="700" r="5" fill={COLORS.tree} />
          <circle cx="130" cy="720" r="6" fill={COLORS.tree} />
          <circle cx="180" cy="710" r="5" fill={COLORS.tree} />
          <circle cx="225" cy="730" r="6" fill={COLORS.tree} />
          <circle cx="100" cy="780" r="5" fill={COLORS.tree} />
          <circle cx="160" cy="790" r="6" fill={COLORS.tree} />
          <circle cx="220" cy="790" r="5" fill={COLORS.tree} />
          <circle cx="60" cy="745" r="3" fill={COLORS.bush} />
          <circle cx="200" cy="755" r="3.5" fill={COLORS.bush} />
          {/* Forest patch */}
          <circle cx="1240" cy="280" r="6" fill={COLORS.tree} />
          <circle cx="1290" cy="270" r="5" fill={COLORS.tree} />
          <circle cx="1340" cy="280" r="6" fill={COLORS.tree} />
          <circle cx="1390" cy="300" r="5" fill={COLORS.tree} />
          <circle cx="1440" cy="310" r="6" fill={COLORS.tree} />
          <circle cx="1260" cy="340" r="5" fill={COLORS.tree} />
          <circle cx="1310" cy="350" r="6" fill={COLORS.tree} />
          <circle cx="1360" cy="370" r="5" fill={COLORS.tree} />
          <circle cx="1420" cy="380" r="6" fill={COLORS.tree} />
          <circle cx="1260" cy="400" r="5" fill={COLORS.tree} />
          <circle cx="1340" cy="410" r="6" fill={COLORS.tree} />
          <circle cx="1280" cy="320" r="3" fill={COLORS.bush} />
          <circle cx="1370" cy="340" r="3.5" fill={COLORS.bush} />
          <circle cx="1420" cy="345" r="3" fill={COLORS.bush} />
          {/* Pocket park */}
          <circle cx="600" cy="900" r="5" fill={COLORS.tree} />
          <circle cx="635" cy="910" r="6" fill={COLORS.tree} />
          <circle cx="670" cy="925" r="5" fill={COLORS.tree} />
          <circle cx="615" cy="935" r="3.5" fill={COLORS.bush} />
          {/* Wedge park */}
          <circle cx="1500" cy="50" r="5" fill={COLORS.tree} />
          <circle cx="1540" cy="60" r="6" fill={COLORS.tree} />
          <circle cx="1580" cy="80" r="5" fill={COLORS.tree} />
          <circle cx="1500" cy="100" r="3.5" fill={COLORS.bush} />
          {/* Lakeshore — shifted down with the lake so the trees still sit
              along its perimeter. */}
          <circle cx="1280" cy="1015" r="4" fill={COLORS.tree} />
          <circle cx="1530" cy="1000" r="5" fill={COLORS.tree} />
          <circle cx="1605" cy="1140" r="4" fill={COLORS.tree} />
          <circle cx="1310" cy="1195" r="5" fill={COLORS.tree} />
          {/* Scattered street trees along major arterials */}
          <circle cx="80" cy="160" r="3" fill={COLORS.tree} />
          <circle cx="180" cy="160" r="3" fill={COLORS.tree} />
          <circle cx="260" cy="160" r="3" fill={COLORS.tree} />
          <circle cx="800" cy="160" r="3" fill={COLORS.tree} />
          <circle cx="880" cy="160" r="3" fill={COLORS.tree} />
          <circle cx="1000" cy="160" r="3" fill={COLORS.tree} />
          <circle cx="1080" cy="160" r="3" fill={COLORS.tree} />
          <circle cx="80" cy="535" r="3" fill={COLORS.tree} />
          <circle cx="180" cy="535" r="3" fill={COLORS.tree} />
          <circle cx="800" cy="535" r="3" fill={COLORS.tree} />
          <circle cx="880" cy="535" r="3" fill={COLORS.tree} />
          <circle cx="80" cy="835" r="3" fill={COLORS.tree} />
          <circle cx="180" cy="835" r="3" fill={COLORS.tree} />
          <circle cx="280" cy="835" r="3" fill={COLORS.tree} />
          <circle cx="800" cy="835" r="3" fill={COLORS.tree} />
          <circle cx="880" cy="835" r="3" fill={COLORS.tree} />
          <circle cx="1000" cy="835" r="3" fill={COLORS.tree} />
          <circle cx="1080" cy="835" r="3" fill={COLORS.tree} />
          <circle cx="1170" cy="535" r="3" fill={COLORS.tree} />
          <circle cx="1500" cy="535" r="3" fill={COLORS.tree} />
        </g>

        {/* 6. FOOTPATHS — dashed trails meander through parks */}
        <g fill="none" strokeWidth="2" strokeDasharray="5 5" stroke={COLORS.footpath}>
          <path d="M 380 280 Q 480 270 580 280 T 720 350" />
          <path d="M 400 460 Q 500 430 600 460 T 720 440" />
          <path d="M 50 720 Q 130 700 200 730 T 280 760" />
          <path d="M 1200 320 Q 1300 300 1400 330 T 1480 350" />
        </g>

        {/* 7. PLAZAS — small roundabouts at major intersections, drawn on
            top of everything so they read as decorative road furniture. */}
        <g>
          <circle cx="358" cy="540" r="20" fill={COLORS.plaza} />
          <circle cx="358" cy="540" r="6" fill={COLORS.plazaCenter} />
          <circle cx="750" cy="850" r="22" fill={COLORS.plaza} />
          <circle cx="750" cy="850" r="7" fill={COLORS.plazaCenter} />
          <circle cx="1155" cy="540" r="18" fill={COLORS.plaza} />
          <circle cx="1155" cy="540" r="6" fill={COLORS.plazaCenter} />
        </g>
      </svg>
    </div>
  );
};
